import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { useAuth } from '../../auth/hooks/useAuth';
import type { Company, CompanyInput } from '@elka/types';

export function useCompanies() {
  const { user } = useAuth();
  const companiesQuery = useMemo(
    () => query(collection(db, ...splitPath(COLLECTIONS.crm.companies)), orderBy('name', 'asc')),
    []
  );

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    async function loadCompanies() {
      try {
        const snapshot = await getDocs(companiesQuery);
        if (!isMounted) return;
        setCompanies(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Company[]
        );
      } catch (err) {
        console.error('[useCompanies] Failed to load CRM companies:', err);
        if (!isMounted) return;
        setError(err instanceof Error ? err : new Error('Failed to load companies'));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    setLoading(true);
    setError(undefined);
    loadCompanies();

    return () => {
      isMounted = false;
    };
  }, [companiesQuery]);

  const addCompany = async (data: CompanyInput): Promise<Company> => {
    if (!user) throw new Error('לא מחובר');

    const payload = {
      ...data,
      userId: user.uid,
      createdBy: user.displayName || user.email || 'Unknown',
      createdAt: serverTimestamp(),
    };
    const docRef = await addDoc(collection(db, ...splitPath(COLLECTIONS.crm.companies)), payload);

    const created = { id: docRef.id, ...payload } as unknown as Company;
    setCompanies((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
    return created;
  };

  return { companies, loading, error, addCompany };
}
